<template>
  <div ref="galleryRef" class="message-gallery">
    <div class="gallery-thumbnails" v-if="showThumbnails && attachments.length > 0">
      <div
        v-for="(attachment, index) in attachments"
        :key="attachment.id || index"
        class="gallery-thumbnail"
        @click="openGallery(index)"
      >
        <img
          :src="attachment.thumbnail || attachment.preview_url || attachment.url"
          :alt="`Image ${index + 1}`"
          class="gallery-thumbnail-img"
          loading="lazy"
        />
        <div class="gallery-thumbnail-overlay">
          <i class="fe fe-search"></i>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <Transition name="gallery-modal" @after-leave="afterClose">
        <div
          v-if="isOpen"
          class="gallery-lightbox"
          @click.self="handleOverlayClick"
          @keydown.esc="closeGallery"
        >
        <div class="gallery-lightbox-content">
          <button class="gallery-lightbox-close" @click="closeGallery">
            <i class="fe fe-x"></i>
          </button>

          <button
            v-if="showPreview && currentIndex > 0"
            type="button"
            class="spotlight-scroll-arrow spotlight-scroll-left gallery-lightbox-nav"
            aria-label="Scroll left"
            @click="navigate(-1)"
          >
            <i class="fe fe-chevron-left"></i>
          </button>
          <button
            v-if="showPreview && currentIndex < attachments.length - 1"
            type="button"
            class="spotlight-scroll-arrow spotlight-scroll-right gallery-lightbox-nav"
            aria-label="Scroll right"
            @click="navigate(1)"
          >
            <i class="fe fe-chevron-right"></i>
          </button>

          <div
            ref="containerRef"
            class="gallery-lightbox-image-container"
            @wheel.prevent="handleWheel"
            @mousedown="handleMouseDown"
            @mouseleave="handleMouseUp"
            @touchstart.prevent="handleTouchStart"
            @touchmove.prevent="handleTouchMove"
            @touchend.prevent="handleTouchEnd"
            @click="handleImageClick"
            @contextmenu.prevent
            @selectstart.prevent
          >
            <img
              :src="currentAttachment?.url"
              :alt="`Image ${currentIndex + 1}`"
              class="gallery-lightbox-image"
              :class="{ 'is-dragging': isDragging || isPinching }"
              :style="imageStyle"
              :draggable="false"
              loading="eager"
              oncontextmenu="return false;"
              @load="onImageLoad"
              @error="onImageError"
            />
            <div v-if="isLoading && !hasError" class="gallery-lightbox-loading">
              <i class="fa fa-spinner fa-spin"></i>
            </div>
            <div v-if="hasError || !currentAttachment?.url" class="gallery-lightbox-error">
              <i class="fe fe-image"></i>
              <div class="gallery-lightbox-error-text">{{ t('messages.gallery.image_load_error') }}</div>
              <div v-if="currentAttachment?.url" class="gallery-lightbox-error-filename">
                {{ getFilename(currentAttachment.url) }}
              </div>
            </div>
          </div>

          <div class="gallery-lightbox-counter">
            {{ currentIndex + 1 }} / {{ attachments.length }}
          </div>

          <div v-if="currentAttachment" class="gallery-lightbox-info">
            <div class="gallery-lightbox-filename">
              {{ getFilename(currentAttachment.url) }}
            </div>
          </div>

          <Transition name="preview-slide">
            <div
              v-if="showPreview && attachments.length > 1"
              class="gallery-lightbox-preview"
              @click.stop
            >
              <div class="gallery-preview-track">
                <div
                  v-for="(attachment, idx) in attachments"
                  :key="attachment.id || idx"
                  class="gallery-preview-thumb"
                  :class="{ 'is-current': idx === currentIndex }"
                  @click="navigateTo(idx)"
                >
                  <img
                    :src="attachment.thumbnail || attachment.preview_url || attachment.url"
                    :alt="`Preview ${idx + 1}`"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import type { CSSProperties } from 'vue';
import { useI18n } from '@/composables/useI18n';

const { t } = useI18n();

interface Attachment {
  id?: string;
  url: string;
  thumbnail?: string;
  preview_url?: string;
  type?: string;
}

const props = withDefaults(defineProps<{
  attachments: Attachment[];
  showThumbnails?: boolean;
}>(), {
  showThumbnails: true,
});

const isOpen = ref(false);
const currentIndex = ref(0);
const isLoading = ref(false);
const hasError = ref(false);
const galleryRef = ref<HTMLElement | null>(null);
const containerRef = ref<HTMLElement | null>(null);
const scale = ref(1);
const translateX = ref(0);
const translateY = ref(0);
const isDragging = ref(false);
const isPinching = ref(false);
const showPreview = ref(false);

let dragStartX = 0;
let dragStartY = 0;
let startTranslateX = 0;
let startTranslateY = 0;
let pinchStartScale = 1;
let startTouchDistance = 0;
let startMidX = 0;
let startMidY = 0;
let hasMoved = false;
let singleClickTimer: number | null = null;
let lastClickTime = 0;
let lastClickX = 0;
let lastClickY = 0;
let touchStartX = 0;
let touchStartY = 0;
let touchStartTime = 0;
let isSwiping = false;
let activeTouchCount = 0;

const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);

const imageStyle = computed((): CSSProperties => ({
  transform: `translate(${translateX.value}px, ${translateY.value}px) scale(${scale.value})`,
  transition: isDragging.value || isPinching.value ? 'none' : 'transform 0.15s ease-out',
  cursor: isDragging.value || isPinching.value ? 'grabbing' : (scale.value > 1 ? 'grab' : 'default'),
  userSelect: 'none',
  WebkitUserSelect: 'none',
}));

const resetZoom = () => {
  scale.value = 1;
  translateX.value = 0;
  translateY.value = 0;
};

const currentAttachment = computed(() => {
  return props.attachments[currentIndex.value];
});

const openGallery = (index: number) => {
  resetZoom();
  showPreview.value = false;
  currentIndex.value = index;
  isOpen.value = true;
  isLoading.value = true;
  hasError.value = false;
  document.body.style.overflow = 'hidden';
  preloadImages(index);
};

const closeGallery = () => {
  if (!isOpen.value) return;
  isOpen.value = false;
  isLoading.value = false;
  document.body.style.overflow = '';
  endDrag();
  endPinch();
};

const afterClose = () => {
  resetZoom();
  showPreview.value = false;
};

const navigate = (direction: number) => {
  const newIndex = currentIndex.value + direction;
  if (newIndex >= 0 && newIndex < props.attachments.length) {
    resetZoom();
    currentIndex.value = newIndex;
    isLoading.value = true;
    hasError.value = false;
    preloadImages(newIndex);
  }
};

const navigateTo = (index: number) => {
  if (index >= 0 && index < props.attachments.length && index !== currentIndex.value) {
    resetZoom();
    currentIndex.value = index;
    isLoading.value = true;
    hasError.value = false;
    preloadImages(index);
  }
};

const preloadImages = (centerIndex: number = currentIndex.value) => {
  const indices = [centerIndex - 1, centerIndex + 1];
  indices.forEach((idx) => {
    if (idx < 0 || idx >= props.attachments.length) return;
    const url = props.attachments[idx]?.url;
    if (!url) return;
    const img = new Image();
    img.src = url;
  });
};

const onImageLoad = () => {
  isLoading.value = false;
  hasError.value = false;
};

const onImageError = () => {
  isLoading.value = false;
  hasError.value = true;
  console.error('Failed to load image:', currentAttachment.value?.url);
};

const getFilename = (url: string): string => {
  return url.split('/').pop() || 'image';
};

const togglePreview = () => {
  showPreview.value = !showPreview.value;
};

const handleOverlayClick = () => {
  if (showPreview.value) {
    showPreview.value = false;
  } else if (scale.value > 1) {
    resetZoom();
  } else {
    closeGallery();
  }
};

const getEventCoordinates = (e: MouseEvent | Touch | TouchEvent): { x: number; y: number } => {
  if ('clientX' in e && typeof (e as any).clientX === 'number') {
    const t = e as MouseEvent | Touch;
    return { x: t.clientX, y: t.clientY };
  }
  const touchEvent = e as TouchEvent;
  const touch = touchEvent.changedTouches?.[0] || touchEvent.touches?.[0];
  return touch ? { x: touch.clientX, y: touch.clientY } : { x: 0, y: 0 };
};

const handleTap = (x: number, y: number) => {
  if (hasMoved) {
    hasMoved = false;
    return;
  }

  const now = Date.now();
  if (
    now - lastClickTime < 250 &&
    Math.hypot(x - lastClickX, y - lastClickY) < 30
  ) {
    if (singleClickTimer) {
      clearTimeout(singleClickTimer);
      singleClickTimer = null;
    }
    lastClickTime = 0;
    if (scale.value > 1.1) {
      resetZoom();
    } else {
      setZoomAround(x, y, 2.5);
    }
    return;
  }

  if (singleClickTimer) {
    clearTimeout(singleClickTimer);
  }

  singleClickTimer = window.setTimeout(() => {
    singleClickTimer = null;
    togglePreview();
  }, 250);

  lastClickTime = now;
  lastClickX = x;
  lastClickY = y;
};

const handleImageClick = (e: MouseEvent) => {
  const { x, y } = getEventCoordinates(e);
  handleTap(x, y);
};

// Zoom / pan helpers
const getContainerCenter = () => {
  const rect = containerRef.value?.getBoundingClientRect();
  if (!rect) return { x: 0, y: 0 };
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
};

const getImageBounds = () => {
  const container = containerRef.value;
  const img = container?.querySelector('.gallery-lightbox-image') as HTMLImageElement | null;
  if (!container || !img || !img.naturalWidth || !img.naturalHeight) {
    return { maxX: 0, maxY: 0 };
  }

  const containerW = container.clientWidth;
  const containerH = container.clientHeight;
  const imageAspect = img.naturalWidth / img.naturalHeight;
  const containerAspect = containerW / containerH;

  let displayW: number;
  let displayH: number;
  if (imageAspect > containerAspect) {
    displayW = containerW;
    displayH = containerW / imageAspect;
  } else {
    displayH = containerH;
    displayW = containerH * imageAspect;
  }

  const scaledW = displayW * scale.value;
  const scaledH = displayH * scale.value;

  return {
    maxX: Math.max(0, (scaledW - containerW) / 2),
    maxY: Math.max(0, (scaledH - containerH) / 2),
  };
};

const clampTranslate = () => {
  if (scale.value <= 1) {
    translateX.value = 0;
    translateY.value = 0;
    return;
  }
  const { maxX, maxY } = getImageBounds();
  translateX.value = clamp(translateX.value, -maxX, maxX);
  translateY.value = clamp(translateY.value, -maxY, maxY);
};

const setZoomAround = (clientX: number, clientY: number, newScale: number) => {
  const center = getContainerCenter();
  const dx = clientX - center.x;
  const dy = clientY - center.y;
  const oldScale = scale.value;
  const s = clamp(newScale, 1, 4);
  scale.value = s;
  translateX.value = dx - (dx - translateX.value) * (s / oldScale);
  translateY.value = dy - (dy - translateY.value) * (s / oldScale);
  clampTranslate();
};

const handleWheel = (e: WheelEvent) => {
  e.preventDefault();
  const delta = -e.deltaY * 0.0015;
  const newScale = scale.value * (1 + delta);
  setZoomAround(e.clientX, e.clientY, newScale);
};

const endDrag = () => {
  isDragging.value = false;
  clampTranslate();
};

const handleMouseDown = (e: MouseEvent) => {
  if (e.button !== 0 || scale.value <= 1) return;
  e.preventDefault();
  isDragging.value = true;
  hasMoved = false;
  dragStartX = e.clientX;
  dragStartY = e.clientY;
  startTranslateX = translateX.value;
  startTranslateY = translateY.value;
};

const handleMouseMove = (e: MouseEvent) => {
  if (!isDragging.value) return;
  e.preventDefault();
  const dx = e.clientX - dragStartX;
  const dy = e.clientY - dragStartY;
  if (Math.hypot(dx, dy) > 5) hasMoved = true;
  translateX.value = startTranslateX + dx;
  translateY.value = startTranslateY + dy;
  clampTranslate();
};

const handleMouseUp = () => {
  endDrag();
};

const getTouchDistance = (touches: TouchList) => {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.hypot(dx, dy);
};

const getTouchMidpoint = (touches: TouchList) => {
  return {
    x: (touches[0].clientX + touches[1].clientX) / 2,
    y: (touches[0].clientY + touches[1].clientY) / 2,
  };
};

const endPinch = () => {
  isPinching.value = false;
  if (scale.value < 1) {
    resetZoom();
  } else {
    clampTranslate();
  }
};

const handleTouchStart = (e: TouchEvent) => {
  activeTouchCount = Math.max(activeTouchCount, e.touches.length);

  if (e.touches.length === 2) {
    e.preventDefault();
    isPinching.value = true;
    startTouchDistance = getTouchDistance(e.touches);
    const mid = getTouchMidpoint(e.touches);
    startMidX = mid.x;
    startMidY = mid.y;
    pinchStartScale = scale.value;
    startTranslateX = translateX.value;
    startTranslateY = translateY.value;
  } else if (e.touches.length === 1) {
    const touch = e.touches[0];
    if (scale.value > 1) {
      isDragging.value = true;
      hasMoved = false;
      dragStartX = touch.clientX;
      dragStartY = touch.clientY;
      startTranslateX = translateX.value;
      startTranslateY = translateY.value;
    } else {
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchStartTime = Date.now();
      isSwiping = false;
      hasMoved = false;
    }
  }
};

const handleTouchMove = (e: TouchEvent) => {
  if (e.touches.length === 2 && isPinching.value) {
    e.preventDefault();
    const newDistance = getTouchDistance(e.touches);
    const newMid = getTouchMidpoint(e.touches);
    const s = clamp(pinchStartScale * (newDistance / startTouchDistance), 1, 4);
    const center = getContainerCenter();
    const dx0 = startMidX - center.x;
    const dy0 = startMidY - center.y;
    const dx1 = newMid.x - center.x;
    const dy1 = newMid.y - center.y;
    translateX.value = dx1 - dx0 * (s / pinchStartScale) + startTranslateX * (s / pinchStartScale);
    translateY.value = dy1 - dy0 * (s / pinchStartScale) + startTranslateY * (s / pinchStartScale);
    scale.value = s;
    clampTranslate();
  } else if (e.touches.length === 1 && isDragging.value) {
    const touch = e.touches[0];
    const dx = touch.clientX - dragStartX;
    const dy = touch.clientY - dragStartY;
    if (Math.hypot(dx, dy) > 5) hasMoved = true;
    translateX.value = startTranslateX + dx;
    translateY.value = startTranslateY + dy;
    clampTranslate();
  } else if (e.touches.length === 1 && !isPinching.value && scale.value === 1) {
    const touch = e.touches[0];
    const dx = touch.clientX - touchStartX;
    const dy = touch.clientY - touchStartY;
    if (Math.hypot(dx, dy) > 10) isSwiping = true;
  }
};

const handleTouchEnd = (e: TouchEvent) => {
  if (isPinching.value && e.touches.length < 2) {
    endPinch();
  }

  if (isDragging.value && e.touches.length === 0) {
    const wasMoved = hasMoved;
    endDrag();
    if (!wasMoved && activeTouchCount === 1) {
      const touch = e.changedTouches[0];
      if (touch) handleTap(touch.clientX, touch.clientY);
    }
    if (e.touches.length === 0) activeTouchCount = 0;
    return;
  }

  if (e.touches.length === 0) {
    if (activeTouchCount > 1) {
      activeTouchCount = 0;
      isSwiping = false;
      return;
    }

    const touch = e.changedTouches[0];
    if (!touch) {
      activeTouchCount = 0;
      return;
    }

    const dx = touch.clientX - touchStartX;
    const dy = touch.clientY - touchStartY;

    if (isSwiping) {
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        navigate(dx < 0 ? 1 : -1);
      } else if (dy > 80 && dy > Math.abs(dx)) {
        if (showPreview.value) {
          showPreview.value = false;
        } else {
          closeGallery();
        }
      }
      isSwiping = false;
    } else if (Math.hypot(dx, dy) < 10) {
      handleTap(touch.clientX, touch.clientY);
    }

    activeTouchCount = 0;
  }
};

// Download removed for security

const handleKeydown = (e: KeyboardEvent) => {
  if (!isOpen.value) return;
  
  switch (e.key) {
    case 'ArrowLeft':
      navigate(-1);
      break;
    case 'ArrowRight':
      navigate(1);
      break;
    case 'Escape':
      closeGallery();
      break;
  }
};

watch(() => props.attachments, () => {
  if (currentIndex.value >= props.attachments.length) {
    currentIndex.value = Math.max(0, props.attachments.length - 1);
  }
});

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseup', handleMouseUp);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
  window.removeEventListener('mousemove', handleMouseMove);
  window.removeEventListener('mouseup', handleMouseUp);
  document.body.style.overflow = '';
  endDrag();
  endPinch();
});

defineExpose({
  openGallery
});
</script>

<style scoped>
.message-gallery {
  margin: 10px 0;
}

.gallery-thumbnails {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
  margin: 8px 0;
}

.gallery-thumbnail {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.gallery-thumbnail:hover {
  transform: scale(1.05);
}

.gallery-thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gallery-thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.gallery-thumbnail:hover .gallery-thumbnail-overlay {
  opacity: 1;
}

.gallery-thumbnail-overlay i {
  color: white;
  font-size: 24px;
}

.gallery-lightbox {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.gallery-lightbox-content {
  position: relative;
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.gallery-lightbox-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.gallery-lightbox-close:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Gallery arrows reuse global .spotlight-scroll-arrow styles */
.gallery-lightbox .spotlight-scroll-arrow,
.gallery-lightbox-nav {
  display: inline-flex;
  width: 56px;
  height: 56px;
  font-size: 28px;
  top: 50%;
}

.gallery-lightbox-image-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: unset;
  overflow: hidden;
  touch-action: none;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

.gallery-lightbox-image {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 0;
  -webkit-user-drag: none;
  transition: transform 0.15s ease-out;
}

.gallery-lightbox-image.is-dragging {
  transition: none;
}

.gallery-lightbox-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 24px;
}

.gallery-lightbox-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
  z-index: 1;
  pointer-events: none;
}

.gallery-lightbox-error .fe {
  font-size: 3rem;
  margin-bottom: 0.5rem;
  opacity: 0.7;
}

.gallery-lightbox-error-text {
  font-size: 1rem;
  margin-bottom: 0.25rem;
}

.gallery-lightbox-error-filename {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  max-width: 80vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gallery-lightbox-counter {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  color: white;
  font-size: 14px;
  background: rgba(0, 0, 0, 0.5);
  padding: 4px 12px;
  border-radius: 12px;
}

.gallery-lightbox-info {
  position: absolute;
  bottom: -40px;
  right: 0;
  color: white;
  font-size: 12px;
  text-align: right;
}

.gallery-lightbox-filename {
  margin-bottom: 8px;
  opacity: 0.8;
}

/* Bottom preview strip overlay */
.gallery-lightbox-preview {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 3;
  padding: 12px 8px 16px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.5) 60%, transparent 100%);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  touch-action: pan-x;
}

.gallery-preview-track {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 4px;
  max-width: 100%;
  scrollbar-width: none;
}

.gallery-preview-track::-webkit-scrollbar {
  display: none;
}

.gallery-preview-thumb {
  flex: 0 0 64px;
  width: 64px;
  height: 64px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
  border: 2px solid transparent;
}

.gallery-preview-thumb.is-current {
  opacity: 1;
  border-color: #fff;
  transform: scale(1.05);
}

.gallery-preview-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media (max-width: 768px) {
  .gallery-lightbox {
    padding: 0;
  }

  .gallery-lightbox-counter,
  .gallery-lightbox-info {
    display: none;
  }
}
</style>

<style>
/* Modal-style open/close animation */
.gallery-modal-enter-active,
.gallery-modal-leave-active {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.35s cubic-bezier(0.32, 0.72, 0, 1);
}

.gallery-modal-enter-from,
.gallery-modal-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.gallery-modal-enter-to,
.gallery-modal-leave-from {
  transform: translateY(0);
  opacity: 1;
}

/* Preview collapse animation */
.preview-slide-enter-active,
.preview-slide-leave-active {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.35s cubic-bezier(0.32, 0.72, 0, 1);
}

.preview-slide-enter-from,
.preview-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.preview-slide-enter-to,
.preview-slide-leave-from {
  transform: translateY(0);
  opacity: 1;
}
</style>
